import { useEffect } from "react";
import { useGame } from "./store";
import { MISCONCEPTIONS } from "./questionBank";
import Mentor from "./Mentor";

export default function FinishScreen() {
  // Completing a lap unlocks the Exam and stamps this phase's RIDE
  const unlockExam = useGame((s) => s.unlockExam);
  const openExam = useGame((s) => s.openExam);
  const openMap = useGame((s) => s.openMap);
  const markProgress = useGame((s) => s.markProgress);
  useEffect(() => {
    unlockExam();
    markProgress("p1Lap");
  }, []);
  const points = useGame((s) => s.points);
  const coins = useGame((s) => s.coins);
  const stations = useGame((s) => s.stationsCrossed);
  const analytics = useGame((s) => s.analytics);
  const globalTimer = useGame((s) => s.globalTimer);
  const misconceptionLog = useGame((s) => s.misconceptionLog);
  const poeStats = useGame((s) => s.poeStats);

  const confirmed = Object.keys(misconceptionLog).filter(
    (t) => misconceptionLog[t] >= 2 && MISCONCEPTIONS[t]
  );
  const occasional = Object.keys(misconceptionLog).filter(
    (t) => misconceptionLog[t] === 1 && MISCONCEPTIONS[t]
  );
  const totalPredictions = Object.values(poeStats).reduce((a, s) => a + s.total, 0);
  const firstTryCorrect = Object.values(poeStats).reduce(
    (a, s) => a + s.firstTryCorrect, 0
  );
  const crashes = useGame((s) => s.crashes);
  const hazardResults = useGame((s) => s.hazardResults);
  const perfects = hazardResults.filter((h) => h.outcome === "perfect").length;
  const headline =
    crashes === 0 && perfects === hazardResults.length && hazardResults.length > 0
      ? "Perfect Physics!"
      : crashes === 0
        ? "Victory!"
        : "Lap Survived!";
  const outcomeStyle = {
    perfect: { color: "#00ffaa", icon: "🏆", text: "Perfect" },
    safe: { color: "#3eb6ff", icon: "✓", text: "Cleared" },
    cautious: { color: "#ffd23f", icon: "🐢", text: "Too slow" },
    risky: { color: "#ff8a3d", icon: "⚠", text: "Over limit!" },
    crashed: { color: "#ff4d6d", icon: "💥", text: "Derailed" },
  };

  // ─── Personalized STUDY PLAN built from this run's play data ───
  const studyPlan = [];
  hazardResults.forEach((h) => {
    const pc = ((100 * h.maxV) / h.vmax).toFixed(0);
    if (h.outcome === "crashed") {
      studyPlan.push({
        topic: "Centripetal force — why curves have speed limits",
        evidence: `You derailed at ${h.label}: entered at ${h.maxV.toFixed(1)} m/s against a limit of ${h.vmax.toFixed(1)} m/s (${pc}%).`,
        study: "Study: uniform circular motion, F꜀ = mv²/r, and why the needed inward force grows with the SQUARE of speed. Try: halve your speed — the force needed drops to a quarter.",
      });
    } else if (h.outcome === "risky") {
      studyPlan.push({
        topic: "Safety margins in circular motion",
        evidence: `At ${h.label} you peaked at ${h.maxV.toFixed(1)} m/s — over the ${h.vmax.toFixed(1)} m/s limit (${pc}%) — and only just held on.`,
        study: "Study: v_max = √(g·r·cosθ) is a hard physical boundary, not a suggestion. Engineers design to ~90% of limits — drive 85–95% of v_max.",
      });
    } else if (h.outcome === "cautious") {
      studyPlan.push({
        topic: "Trusting quantitative predictions",
        evidence: `At ${h.label} you crossed at only ${pc}% of the safe limit (${h.maxV.toFixed(1)} vs ${h.vmax.toFixed(1)} m/s).`,
        study: "If the equation says 19 m/s is safe, then 17 m/s IS safe — that's the power of physics: you can know before you try. Practice estimating √(g·r) mentally.",
      });
    }
  });
  if (totalPredictions > 0 && firstTryCorrect < totalPredictions) {
    studyPlan.push({
      topic: "Free-body diagrams at curves and crests",
      evidence: `${totalPredictions - firstTryCorrect} of your ${totalPredictions} reasoning answers needed retries.`,
      study: "Study: draw the FBD before every problem — only N, mg and friction act on the cart; their inward resultant must equal mv²/r. No 'centrifugal' arrow!",
    });
  }

  return (
    <div
      data-testid="finish-screen"
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(2, 4, 12, 0.92)",
        backdropFilter: "blur(12px)",
        zIndex: 30,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        color: "#fff",
        fontFamily: "'Inter', system-ui, sans-serif",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <div
        style={{
          width: "min(520px, 100%)",
          textAlign: "center",
          padding: 40,
        }}
      >
        <div
          style={{
            fontSize: 13,
            letterSpacing: 6,
            color: "#00ffaa",
            fontWeight: 600,
          }}
        >
          LAP COMPLETE
        </div>
        <h1
          style={{
            fontSize: 64,
            margin: "12px 0 20px",
            background:
              "linear-gradient(90deg, #00ffaa 0%, #3eb6ff 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontWeight: 900,
          }}
        >
          {headline}
        </h1>
        {crashes > 0 && (
          <div style={{ color: "#ff8a9b", fontSize: 14, marginBottom: 10 }}>
            {crashes} derailment{crashes > 1 ? "s" : ""} on the way — check the curve report below.
          </div>
        )}

        {/* Mentor's verdict */}
        <Mentor
          mood={crashes > 0 ? "thinking" : "proud"}
          text={
            crashes > 0
              ? "Every derailment you EXPLAINED made you a better engineer. Re-ride for a clean lap, then face my exam."
              : "Flawless circular motion driving! Now prove it on paper — the exam stamp awaits on the Park Map."
          }
        />

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 16,
            marginTop: 20,
            marginBottom: 30,
          }}
        >
          <div
            style={{
              padding: 18,
              background: "rgba(255,255,255,0.04)",
              borderRadius: 14,
            }}
          >
            <div style={{ fontSize: 12, color: "#9aa4b2", letterSpacing: 1.5 }}>
              POINTS
            </div>
            <div style={{ fontSize: 32, color: "#00ffaa", fontWeight: 800 }}>
              {points}
            </div>
          </div>
          <div
            style={{
              padding: 18,
              background: "rgba(255,255,255,0.04)",
              borderRadius: 14,
            }}
          >
            <div style={{ fontSize: 12, color: "#9aa4b2", letterSpacing: 1.5 }}>
              COINS
            </div>
            <div style={{ fontSize: 32, color: "#ffd23f", fontWeight: 800 }}>
              {coins}
            </div>
          </div>
          <div
            style={{
              padding: 18,
              background: "rgba(255,255,255,0.04)",
              borderRadius: 14,
            }}
          >
            <div style={{ fontSize: 12, color: "#9aa4b2", letterSpacing: 1.5 }}>
              STATIONS
            </div>
            <div style={{ fontSize: 32, color: "#3eb6ff", fontWeight: 800 }}>
              {stations}/5
            </div>
          </div>
        </div>

        {/* --- PHYSICS ANALYTICS --- */}
        <div style={{ background: "rgba(0,0,0,0.4)", borderRadius: 14, padding: 20, marginBottom: 30, textAlign: "left", fontSize: 14 }}>
          <div style={{ color: "#e879f9", fontWeight: 700, marginBottom: 12, letterSpacing: 1 }}>RIDE ANALYTICS</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 8 }}>
            <span style={{ color: "#9aa4b2" }}>Total Journey Time:</span>
            <span style={{ color: "#fff", textAlign: "right" }}>{globalTimer.toFixed(1)} s</span>
            
            <span style={{ color: "#9aa4b2" }}>Average Speed:</span>
            <span style={{ color: "#fff", textAlign: "right" }}>{(1202 / Math.max(1, globalTimer)).toFixed(1)} m/s</span>

            <span style={{ color: "#9aa4b2" }}>Max Speed Reached:</span>
            <span style={{ color: "#ffd23f", textAlign: "right" }}>{analytics.maxSpeed.toFixed(1)} m/s</span>

            <span style={{ color: "#9aa4b2" }}>Curves Cleared:</span>
            <span style={{ color: "#fff", textAlign: "right" }}>
              {hazardResults.filter((h) => h.outcome !== "crashed").length}/{hazardResults.length || 3}
            </span>

            <span style={{ color: "#9aa4b2" }}>Derailments:</span>
            <span style={{ color: crashes > 0 ? "#ff4d6d" : "#00ffaa", textAlign: "right" }}>
              {crashes > 0 ? `${crashes} 💥` : "None — clean run!"}
            </span>
          </div>
        </div>

        {/* --- CURVE-BY-CURVE DRIVING REPORT --- */}
        {hazardResults.length > 0 && (
          <div
            data-testid="curve-report"
            style={{
              background: "rgba(0,0,0,0.4)",
              borderRadius: 14,
              padding: 20,
              marginBottom: 20,
              textAlign: "left",
              fontSize: 13.5,
            }}
          >
            <div style={{ color: "#ff8a3d", fontWeight: 700, marginBottom: 12, letterSpacing: 1 }}>
              CURVE REPORT — DID YOU DRIVE THE PHYSICS?
            </div>
            {hazardResults.map((h) => {
              const st = outcomeStyle[h.outcome] || outcomeStyle.safe;
              return (
                <div
                  key={h.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 0",
                    borderBottom: "1px solid rgba(255,255,255,0.07)",
                  }}
                >
                  <span style={{ color: "#cfd6e3" }}>{h.label}</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "#9aa4b2" }}>
                    you: {h.maxV.toFixed(1)} / limit: {h.vmax.toFixed(1)} m/s
                  </span>
                  <span style={{ color: st.color, fontWeight: 700, minWidth: 90, textAlign: "right" }}>
                    {st.icon} {st.text}
                  </span>
                </div>
              );
            })}
            <div style={{ marginTop: 10, fontSize: 12, color: "#9aa4b2" }}>
              🏆 Perfect = within 85–100% of v_max: fast AND safe — that's real physics mastery.
            </div>
          </div>
        )}

        {/* --- PERSONALIZED STUDY PLAN (from this run's play data) --- */}
        <div
          data-testid="study-plan"
          style={{
            background: "rgba(0,0,0,0.4)",
            borderRadius: 14,
            padding: 20,
            marginBottom: 20,
            textAlign: "left",
            fontSize: 13.5,
          }}
        >
          <div style={{ color: "#e879f9", fontWeight: 700, marginBottom: 12, letterSpacing: 1 }}>
            📚 YOUR STUDY PLAN — BUILT FROM HOW YOU DROVE
          </div>
          {studyPlan.length === 0 ? (
            <div style={{ color: "#00ffaa" }}>
              ✓ Flawless reasoning and flawless driving — nothing urgent to revise.
              Ready for the next challenge!
            </div>
          ) : (
            studyPlan.map((rec, i) => (
              <div
                key={i}
                style={{
                  background: "rgba(232,121,249,0.07)",
                  border: "1px solid rgba(232,121,249,0.25)",
                  borderRadius: 10,
                  padding: "12px 14px",
                  marginBottom: 10,
                }}
              >
                <div style={{ color: "#e879f9", fontWeight: 700, fontSize: 13 }}>
                  {i + 1}. {rec.topic}
                </div>
                <div style={{ color: "#cfd6e3", fontSize: 12.5, lineHeight: 1.6, marginTop: 4 }}>
                  <b style={{ color: "#ffd23f" }}>From your play:</b> {rec.evidence}
                </div>
                <div style={{ color: "#9fe8c9", fontSize: 12.5, lineHeight: 1.6, marginTop: 4 }}>
                  {rec.study}
                </div>
              </div>
            ))
          )}
        </div>

        {/* --- PHYSICS UNDERSTANDING REPORT --- */}
        <div
          data-testid="understanding-report"
          style={{
            background: "rgba(0,0,0,0.4)",
            borderRadius: 14,
            padding: 20,
            marginBottom: 30,
            textAlign: "left",
            fontSize: 14,
          }}
        >
          <div style={{ color: "#3eb6ff", fontWeight: 700, marginBottom: 12, letterSpacing: 1 }}>
            PHYSICS UNDERSTANDING
          </div>

          {totalPredictions > 0 && (
            <div style={{ marginBottom: 14 }}>
              <span style={{ color: "#9aa4b2" }}>Reasoning correct on first try: </span>
              <span style={{ color: firstTryCorrect === totalPredictions ? "#00ffaa" : "#ffd23f", fontWeight: 700 }}>
                {firstTryCorrect} / {totalPredictions}
              </span>
            </div>
          )}

          {confirmed.length === 0 && occasional.length === 0 ? (
            <div style={{ color: "#00ffaa" }}>
              ✓ No misconceptions detected — solid physical reasoning!
            </div>
          ) : (
            <>
              {confirmed.map((tag) => (
                <div
                  key={tag}
                  style={{
                    background: "rgba(255,77,109,0.1)",
                    border: "1px solid rgba(255,77,109,0.35)",
                    borderRadius: 10,
                    padding: "10px 14px",
                    marginBottom: 10,
                  }}
                >
                  <div style={{ color: "#ff8a9b", fontWeight: 700, fontSize: 13 }}>
                    ⚠ Work on: {MISCONCEPTIONS[tag].label}
                  </div>
                  <div style={{ color: "#cfd6e3", fontSize: 12.5, lineHeight: 1.6, marginTop: 4 }}>
                    {MISCONCEPTIONS[tag].description}
                  </div>
                  <div style={{ color: "#ffd23f", fontSize: 12.5, lineHeight: 1.6, marginTop: 4 }}>
                    💡 {MISCONCEPTIONS[tag].remedy}
                  </div>
                </div>
              ))}
              {occasional.length > 0 && (
                <div style={{ color: "#9aa4b2", fontSize: 12.5, lineHeight: 1.6 }}>
                  Watch out for:{" "}
                  {occasional.map((t) => MISCONCEPTIONS[t].label).join(", ")} (slipped once)
                </div>
              )}
            </>
          )}
        </div>

        {/* --- PARK MAP --- */}
        <button
          data-testid="open-map-btn"
          onClick={openMap}
          style={{
            width: "100%",
            padding: "14px",
            marginBottom: 12,
            background: "rgba(255,210,63,0.12)",
            color: "#ffd23f",
            border: "1px solid rgba(255,210,63,0.45)",
            borderRadius: 12,
            fontSize: 15,
            fontWeight: 800,
            cursor: "pointer",
            letterSpacing: 1,
          }}
        >
          🗺 PARK MAP — see your license & next phase
        </button>

        {/* --- EXAM UNLOCKED --- */}
        <button
          data-testid="open-exam-btn"
          onClick={() => openExam()}
          style={{
            width: "100%",
            padding: "16px",
            marginBottom: 14,
            background: "linear-gradient(135deg, #3eb6ff 0%, #e879f9 100%)",
            color: "#0a0c1e",
            border: "none",
            borderRadius: 12,
            fontSize: 17,
            fontWeight: 900,
            cursor: "pointer",
            letterSpacing: 1,
          }}
        >
          📝 TAKE THE PHYSICS TEST — unlocked!
        </button>

      </div>
    </div>
  );
}
